import { useState } from 'react';
import {
  Bot,
  Key,
  Copy,
  CheckCircle,
  RefreshCw,
  Download,
  Monitor,
  Terminal,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Zap,
  Shield,
  Clock,
  HelpCircle,
  Globe,
  Users,
  Lock,
} from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from '../config';
import { useAuth } from '../components/AuthProvider';
import ApiKeyManagement from '../components/ApiKeyManagement';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is MCP?',
    answer:
      'MCP (Model Context Protocol) is a way to connect AI assistants like Claude to your tools and data. It allows Claude to access DonateMate information directly, making it easier to get help with content management, translations, and other admin tasks.',
  },
  {
    question: 'What authentication methods are supported?',
    answer:
      'We support three authentication methods: (1) OAuth 2.1 for Claude.ai Web - the most secure option with login via browser, (2) API Keys for Claude Code and automation - long-lived tokens, and (3) JWT tokens for personal sessions - short-lived 24h tokens.',
  },
  {
    question: 'How does OAuth work with Claude.ai Web?',
    answer:
      'When you add the MCP server URL to Claude.ai, it automatically discovers our OAuth server. You\'ll be redirected to sign in with your DonateMate MCP credentials. Once authenticated, Claude.ai receives a secure token to access the MCP server on your behalf.',
  },
  {
    question: 'What is the difference between OAuth and API keys?',
    answer:
      'OAuth is browser-based authentication - you sign in through a login page and tokens are managed automatically. API keys are static secrets you manage yourself. OAuth is more secure for browser use (Claude.ai Web), while API keys are better for CLI tools (Claude Code) and automation.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'Yes. OAuth uses industry-standard PKCE flow with short-lived tokens. API keys are stored as secure hashes and can be rotated or revoked at any time. All communication is encrypted via HTTPS. Rate limiting protects against abuse.',
  },
  {
    question: 'What can Claude help me with?',
    answer:
      'Once connected, Claude can help you search design tokens, view content configurations, understand translation keys, and provide guidance on DonateMate features - all without leaving the conversation.',
  },
  {
    question: 'How do I get OAuth access for Claude.ai Web?',
    answer:
      'Contact your administrator to create an MCP OAuth account for you. Once created, you\'ll receive credentials to sign in when connecting Claude.ai to the DonateMate MCP server.',
  },
  {
    question: 'My token expired. What do I do?',
    answer:
      'For OAuth (Claude.ai Web), you\'ll be automatically prompted to sign in again. For JWT tokens, click "Refresh Token" to generate a new one. For API keys, you can create a new key or rotate an existing one from the Admin section below.',
  },
];

// Root admin emails that can manage API keys
const ROOT_ADMIN_EMAILS = ['andrew.sheehy@donate-mate.com', 'admin@donate-mate.com'];

export default function MCPIntegration() {
  const { user } = useAuth();
  const [mcpToken, setMcpToken] = useState<string | null>(null);
  const [mcpTokenExpiry, setMcpTokenExpiry] = useState<Date | null>(null);
  const [isFetchingToken, setIsFetchingToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [configCopied, setConfigCopied] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'web' | 'code' | 'desktop'>('web');
  const [authMethod, setAuthMethod] = useState<'api-key' | 'jwt'>('api-key');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showAdminSection, setShowAdminSection] = useState(true); // Default open
  const [showOAuthSection, setShowOAuthSection] = useState(false);

  // OAuth configuration (from deployed infrastructure)
  const oauthConfig = {
    userPoolId: 'us-east-2_IO0jDqrUE',
    clientId: '1407ru1fu9h0h4ig6urt5sqieh',
    domain: 'donatemate-mcp-staging.auth.us-east-2.amazoncognito.com',
    region: 'us-east-2',
  };

  // Only root admins can manage API keys
  const isRootAdmin = user?.email && ROOT_ADMIN_EMAILS.includes(user.email.toLowerCase());
  const canManageKeys = isRootAdmin;

  // Use the custom domain for HTTP endpoint
  const httpEndpoint = config.mcp.httpEndpoint;
  const wsEndpoint = config.mcp.wsEndpoint;

  const fetchMcpToken = async (forceRefresh = false) => {
    setIsFetchingToken(true);
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const accessToken = session.tokens?.accessToken;
      if (accessToken) {
        setMcpToken(accessToken.toString());
        const payload = accessToken.payload;
        const exp = payload?.exp as number | undefined;
        if (exp) {
          setMcpTokenExpiry(new Date(exp * 1000));
        } else {
          setMcpTokenExpiry(new Date(Date.now() + 24 * 60 * 60 * 1000));
        }
      }
    } catch (error) {
      console.error('Failed to fetch MCP token:', error);
    } finally {
      setIsFetchingToken(false);
    }
  };

  const copyMcpToken = async () => {
    if (mcpToken) {
      await navigator.clipboard.writeText(mcpToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  const getClaudeWebConfig = () => {
    const token = authMethod === 'api-key' ? apiKeyInput || 'YOUR_API_KEY' : mcpToken || 'YOUR_JWT_TOKEN';

    return JSON.stringify(
      {
        url: httpEndpoint,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      null,
      2
    );
  };

  const getClaudeCodeConfig = () => {
    const token = authMethod === 'api-key' ? apiKeyInput || 'YOUR_API_KEY' : mcpToken || 'YOUR_JWT_TOKEN';

    return JSON.stringify(
      {
        mcpServers: {
          donatemate: {
            url: httpEndpoint,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        },
      },
      null,
      2
    );
  };

  const getDesktopConfig = () => {
    const token = authMethod === 'api-key' ? apiKeyInput || 'YOUR_API_KEY' : mcpToken || 'YOUR_JWT_TOKEN';
    const tokenUrl = `${wsEndpoint}?token=${token}`;

    return JSON.stringify(
      {
        mcpServers: {
          donatemate: {
            command: 'npx',
            args: ['-y', 'mcp-remote@latest', tokenUrl],
          },
        },
      },
      null,
      2
    );
  };

  const copyConfig = async () => {
    let configJson: string;
    if (activeTab === 'web') {
      configJson = getClaudeWebConfig();
    } else if (activeTab === 'code') {
      configJson = getClaudeCodeConfig();
    } else {
      configJson = getDesktopConfig();
    }
    await navigator.clipboard.writeText(configJson);
    setConfigCopied(true);
    setTimeout(() => setConfigCopied(false), 2000);
  };

  const getTimeUntilExpiry = () => {
    if (!mcpTokenExpiry) return null;
    const now = new Date();
    const diff = mcpTokenExpiry.getTime() - now.getTime();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m remaining`;
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 mb-4">
          <Bot className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">AI Assistant Integration</h1>
        <p className="text-gray-text text-lg">
          Connect Claude to DonateMate for smarter content management
        </p>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-card p-4 text-center">
          <Zap className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
          <h3 className="text-white font-medium mb-1">Faster Workflows</h3>
          <p className="text-gray-text text-sm">Get instant help with content and translations</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Shield className="w-6 h-6 text-green-400 mx-auto mb-2" />
          <h3 className="text-white font-medium mb-1">Secure Access</h3>
          <p className="text-gray-text text-sm">API keys with rotation and rate limiting</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <h3 className="text-white font-medium mb-1">Long-lived Keys</h3>
          <p className="text-gray-text text-sm">API keys valid for 30-365 days</p>
        </div>
      </div>

      {/* Step 1: Choose Auth Method */}
      <div className="glass-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
            1
          </div>
          <h2 className="text-xl font-semibold text-white">Choose Authentication Method</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <button
            onClick={() => setAuthMethod('api-key')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              authMethod === 'api-key'
                ? 'border-primary bg-primary/10'
                : 'border-gray-border hover:border-gray-text'
            }`}
          >
            <Key className={`w-6 h-6 mb-2 ${authMethod === 'api-key' ? 'text-primary' : 'text-gray-text'}`} />
            <h3 className="text-white font-medium">API Key (Recommended)</h3>
            <p className="text-gray-text text-sm mt-1">
              Long-lived keys (30-365 days). Best for Claude Code and automation.
            </p>
          </button>

          <button
            onClick={() => setAuthMethod('jwt')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              authMethod === 'jwt'
                ? 'border-primary bg-primary/10'
                : 'border-gray-border hover:border-gray-text'
            }`}
          >
            <Shield className={`w-6 h-6 mb-2 ${authMethod === 'jwt' ? 'text-primary' : 'text-gray-text'}`} />
            <h3 className="text-white font-medium">JWT Token</h3>
            <p className="text-gray-text text-sm mt-1">
              Short-lived (24h). Tied to your personal session.
            </p>
          </button>
        </div>

        {authMethod === 'api-key' ? (
          <div className="bg-gray-800/50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-text mb-2">
              Enter your API key
            </label>
            <input
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder="dm_..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-border rounded-lg text-white focus:outline-none focus:border-primary font-mono"
            />
            <p className="text-sm text-gray-text mt-2">
              {isRootAdmin ? (
                <>
                  Create or manage API keys in the{' '}
                  <button
                    onClick={() => setShowAdminSection(true)}
                    className="text-primary hover:underline"
                  >
                    Admin section
                  </button>{' '}
                  below.
                </>
              ) : (
                'Contact your administrator to get an API key.'
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {!mcpToken ? (
              <button
                onClick={() => fetchMcpToken()}
                disabled={isFetchingToken}
                className="btn-primary flex items-center space-x-2"
              >
                {isFetchingToken ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
                <span>{isFetchingToken ? 'Generating...' : 'Generate JWT Token'}</span>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-800 rounded-lg p-3 font-mono text-sm text-white overflow-x-auto">
                    {mcpToken.substring(0, 60)}...
                  </div>
                  <button
                    onClick={copyMcpToken}
                    className={`p-3 rounded-lg transition-colors ${
                      tokenCopied ? 'bg-green-500/20 text-green-400' : 'bg-gray-800 text-gray-text hover:text-white'
                    }`}
                    title="Copy token"
                  >
                    {tokenCopied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => fetchMcpToken(true)}
                    disabled={isFetchingToken}
                    className="p-3 rounded-lg bg-gray-800 text-gray-text hover:text-white transition-colors disabled:opacity-50"
                    title="Refresh token"
                  >
                    <RefreshCw className={`w-5 h-5 ${isFetchingToken ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {mcpTokenExpiry && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-text" />
                    <span className="text-gray-text">
                      Expires: {mcpTokenExpiry.toLocaleString()} ({getTimeUntilExpiry()})
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2: Install Claude */}
      <div className="glass-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
            2
          </div>
          <h2 className="text-xl font-semibold text-white">Install Claude</h2>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setActiveTab('web')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'web'
                ? 'bg-primary text-white'
                : 'bg-gray-800 text-gray-text hover:text-white'
            }`}
          >
            <ExternalLink className="w-4 h-4" />
            <span>Claude.ai Web</span>
          </button>
          <button
            onClick={() => setActiveTab('code')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'code'
                ? 'bg-primary text-white'
                : 'bg-gray-800 text-gray-text hover:text-white'
            }`}
          >
            <Terminal className="w-4 h-4" />
            <span>Claude Code CLI</span>
          </button>
          <button
            onClick={() => setActiveTab('desktop')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'desktop'
                ? 'bg-primary text-white'
                : 'bg-gray-800 text-gray-text hover:text-white'
            }`}
          >
            <Monitor className="w-4 h-4" />
            <span>Desktop App</span>
          </button>
        </div>

        {activeTab === 'web' && (
          <div className="space-y-4">
            <p className="text-gray-text">
              Claude.ai is the web interface at{' '}
              <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                claude.ai
              </a>
              . It supports remote MCP servers directly in the browser.
            </p>
            <a
              href="https://claude.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 btn-primary"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Open Claude.ai</span>
            </a>
          </div>
        )}

        {activeTab === 'code' && (
          <div className="space-y-4">
            <p className="text-gray-text">
              Claude Code is a CLI tool that brings Claude directly to your terminal. It supports
              remote MCP servers with HTTP transport.
            </p>
            <a
              href="https://claude.ai/download"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 btn-primary"
            >
              <Download className="w-4 h-4" />
              <span>Install Claude Code</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {activeTab === 'desktop' && (
          <div className="space-y-4">
            <p className="text-gray-text">
              Claude Desktop is a native app for macOS and Windows. It requires a bridge tool to
              connect to remote MCP servers.
            </p>
            <a
              href="https://claude.ai/download"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 btn-primary"
            >
              <Download className="w-4 h-4" />
              <span>Download Claude Desktop</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>

      {/* Step 3: Configure */}
      <div className="glass-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
            3
          </div>
          <h2 className="text-xl font-semibold text-white">Configure Claude</h2>
        </div>

        {activeTab === 'web' && (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Lock className="w-5 h-5 text-green-400" />
                <h4 className="text-green-400 font-medium">OAuth 2.1 Authentication Ready</h4>
              </div>
              <p className="text-green-300 text-sm">
                Claude.ai Web uses secure OAuth 2.1 authentication. Simply add the server URL below
                and you'll be prompted to sign in with your MCP credentials.
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <h4 className="text-white font-medium">Setup Instructions:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-text">
                <li>Copy the MCP Server URL below</li>
                <li>Go to <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">claude.ai</a> and open Settings</li>
                <li>Navigate to the "MCP Servers" or "Integrations" section</li>
                <li>Click "Add Server" and paste the URL</li>
                <li>When prompted, sign in with your DonateMate MCP credentials</li>
                <li>Once connected, DonateMate tools will be available in your conversations</li>
              </ol>
            </div>

            <div>
              <label className="block text-sm text-gray-text mb-2">MCP Server URL:</label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 bg-gray-900 rounded-lg p-3 text-sm text-green-400 overflow-x-auto font-mono">
                  {httpEndpoint}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(httpEndpoint);
                    setConfigCopied(true);
                    setTimeout(() => setConfigCopied(false), 2000);
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    configCopied
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 text-gray-text hover:text-white'
                  }`}
                >
                  {configCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="w-5 h-5 text-blue-400" />
                <h4 className="text-blue-400 font-medium">Need MCP Access?</h4>
              </div>
              <p className="text-blue-300 text-sm">
                OAuth accounts are invite-only. Contact your administrator to get MCP credentials
                for Claude.ai Web access.
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <h4 className="text-white font-medium">How OAuth Works:</h4>
              <div className="space-y-2 text-gray-text text-sm">
                <p>
                  <strong className="text-primary">1. Discovery:</strong> Claude.ai automatically finds our OAuth server
                </p>
                <p>
                  <strong className="text-primary">2. Authentication:</strong> You sign in via secure Cognito login
                </p>
                <p>
                  <strong className="text-primary">3. Authorization:</strong> Tokens are issued with MCP-specific scopes
                </p>
                <p>
                  <strong className="text-primary">4. Access:</strong> Claude.ai uses tokens to call MCP tools on your behalf
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'code' && (
          <div className="space-y-4">
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <h4 className="text-white font-medium">For Claude Code:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-text">
                <li>
                  Open your Claude Code settings file at{' '}
                  <code className="text-primary">~/.claude/settings.json</code>
                </li>
                <li>Add the configuration below to the mcpServers section</li>
                <li>Restart Claude Code</li>
              </ol>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-text">Configuration:</span>
                <button
                  onClick={copyConfig}
                  disabled={authMethod === 'api-key' ? !apiKeyInput : !mcpToken}
                  className={`flex items-center space-x-1 text-sm px-3 py-1 rounded transition-colors ${
                    configCopied
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 text-gray-text hover:text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {configCopied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy Config</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-sm font-mono">
                <code className="text-green-400">{getClaudeCodeConfig()}</code>
              </pre>
            </div>
          </div>
        )}

        {activeTab === 'desktop' && (
          <div className="space-y-4">
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <h4 className="text-white font-medium">For Claude Desktop:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-text">
                <li>Open Claude Desktop Settings (gear icon)</li>
                <li>Click on Developer tab</li>
                <li>Click Edit Config to open the configuration file</li>
                <li>Add the configuration below</li>
                <li>Save and restart Claude Desktop</li>
              </ol>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-400 text-sm">
                Claude Desktop requires Node.js to be installed for the mcp-remote bridge.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-text">Configuration:</span>
                <button
                  onClick={copyConfig}
                  disabled={authMethod === 'api-key' ? !apiKeyInput : !mcpToken}
                  className={`flex items-center space-x-1 text-sm px-3 py-1 rounded transition-colors ${
                    configCopied
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 text-gray-text hover:text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {configCopied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy Config</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-sm font-mono">
                <code className="text-green-400">{getDesktopConfig()}</code>
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Step 4: Verify */}
      <div className="glass-card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
            4
          </div>
          <h2 className="text-xl font-semibold text-white">Verify the Connection</h2>
        </div>

        <p className="text-gray-text mb-4">
          After configuring Claude, you should see DonateMate tools available. Try asking Claude:
        </p>

        <div className="bg-gray-800/50 rounded-lg p-4">
          <ul className="space-y-2 text-gray-text">
            <li className="flex items-start space-x-2">
              <span className="text-primary">"</span>
              <span>What design tokens are available in DonateMate?</span>
              <span className="text-primary">"</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-primary">"</span>
              <span>Show me the color tokens for the primary brand</span>
              <span className="text-primary">"</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-primary">"</span>
              <span>Search for button-related tokens</span>
              <span className="text-primary">"</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Admin Section - API Key Management */}
      {canManageKeys && (
        <div className="glass-card p-6">
          <button
            onClick={() => setShowAdminSection(!showAdminSection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              <Key className="w-6 h-6 text-primary" />
              <div className="text-left">
                <h2 className="text-xl font-semibold text-white">Admin: API Key Management</h2>
                <p className="text-sm text-gray-text">Create, rotate, and revoke API keys for Claude Code</p>
              </div>
            </div>
            {showAdminSection ? (
              <ChevronUp className="w-5 h-5 text-gray-text" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-text" />
            )}
          </button>

          {showAdminSection && (
            <div className="mt-6 pt-6 border-t border-gray-border">
              <ApiKeyManagement />
            </div>
          )}
        </div>
      )}

      {/* Admin Section - OAuth User Management */}
      {canManageKeys && (
        <div className="glass-card p-6">
          <button
            onClick={() => setShowOAuthSection(!showOAuthSection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              <Globe className="w-6 h-6 text-green-400" />
              <div className="text-left">
                <h2 className="text-xl font-semibold text-white">Admin: OAuth Configuration</h2>
                <p className="text-sm text-gray-text">OAuth 2.1 setup for Claude.ai Web access</p>
              </div>
            </div>
            {showOAuthSection ? (
              <ChevronUp className="w-5 h-5 text-gray-text" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-text" />
            )}
          </button>

          {showOAuthSection && (
            <div className="mt-6 pt-6 border-t border-gray-border space-y-6">
              {/* OAuth Status */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">OAuth 2.1 Active</span>
                </div>
                <p className="text-green-300 text-sm mt-1">
                  Claude.ai Web can authenticate via OAuth. Users need MCP credentials to connect.
                </p>
              </div>

              {/* OAuth Configuration Details */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">OAuth Configuration</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <label className="block text-xs text-gray-text mb-1">User Pool ID</label>
                    <code className="text-sm text-white font-mono">{oauthConfig.userPoolId}</code>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <label className="block text-xs text-gray-text mb-1">Client ID</label>
                    <code className="text-sm text-white font-mono">{oauthConfig.clientId}</code>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3 md:col-span-2">
                    <label className="block text-xs text-gray-text mb-1">OAuth Domain</label>
                    <code className="text-sm text-white font-mono">https://{oauthConfig.domain}</code>
                  </div>
                </div>
              </div>

              {/* OAuth Endpoints */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">OAuth Endpoints</h3>

                <div className="space-y-2 text-sm">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <label className="block text-xs text-gray-text mb-1">Resource Metadata</label>
                    <code className="text-primary font-mono break-all">
                      https://mcp.donate-mate.com/.well-known/oauth-protected-resource
                    </code>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <label className="block text-xs text-gray-text mb-1">Authorization Endpoint</label>
                    <code className="text-white font-mono break-all">
                      https://{oauthConfig.domain}/oauth2/authorize
                    </code>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <label className="block text-xs text-gray-text mb-1">Token Endpoint</label>
                    <code className="text-white font-mono break-all">
                      https://{oauthConfig.domain}/oauth2/token
                    </code>
                  </div>
                </div>
              </div>

              {/* Manage Users */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">Manage OAuth Users</h3>

                <p className="text-gray-text text-sm">
                  OAuth users are managed in AWS Cognito. Use the AWS Console to create, disable, or delete users.
                </p>

                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://${oauthConfig.region}.console.aws.amazon.com/cognito/v2/idp/user-pools/${oauthConfig.userPoolId}/users?region=${oauthConfig.region}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    <span>Manage Users in AWS Console</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-400 text-sm">
                    <strong>Note:</strong> Self-signup is disabled. Admins must create user accounts manually in the AWS Console.
                  </p>
                </div>
              </div>

              {/* OAuth Scopes */}
              <div className="space-y-4">
                <h3 className="text-white font-medium">Supported Scopes</h3>

                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">openid</span>
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">profile</span>
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">email</span>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">mcp.donate-mate.com/read</span>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">mcp.donate-mate.com/write</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FAQ Section */}
      <div className="glass-card p-6">
        <div className="flex items-center space-x-3 mb-6">
          <HelpCircle className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-semibold text-white">Frequently Asked Questions</h2>
        </div>

        <div className="space-y-2">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-border rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-800/50 transition-colors"
              >
                <span className="text-white font-medium">{faq.question}</span>
                {expandedFaq === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-text" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-text" />
                )}
              </button>
              {expandedFaq === index && (
                <div className="px-4 pb-4 text-gray-text">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div className="text-center text-gray-text text-sm">
        <p>
          Need help? Contact your administrator or reach out to{' '}
          <a href="mailto:support@donate-mate.com" className="text-primary hover:underline">
            support@donate-mate.com
          </a>
        </p>
      </div>
    </div>
  );
}
